import { canUseDOM } from "lib/helpers"
import { CURRENT_REGION } from "lib/environment"
import { ISOCountry, ISOCountryFactory } from "cookiesConsent/domain/ISOCountry"
import {
  gpcSignalled,
  gpcNotSignalled,
  PrivacySignal
} from "cookiesConsent/domain/jurisdiction"
import { VisitorPrivacyContext } from "cookiesConsent/domain/VisitorPrivacyContext"

const unknownCountry = `XX`

class StorefrontCountry {
  private readonly byRegion: Record<string, string> = {
    uk: `GB`,
    eu: `DE`,
    us: `US`,
    ca: `CA`,
    au: `AU`,
    row: `US`
  }

  country = (): ISOCountry =>
    new ISOCountryFactory().fromString(
      this.byRegion[CURRENT_REGION || `row`] || `US`
    )
}

export class GeoCookie {
  private readonly country: ISOCountry
  private readonly signal: PrivacySignal

  constructor(country: ISOCountry, signal: PrivacySignal) {
    this.country = country
    this.signal = signal
  }

  context = (): VisitorPrivacyContext => ({
    country: this.country,
    signal: this.signal
  })
}

export class NoGeoCookie {
  context = (): VisitorPrivacyContext => ({
    country: new StorefrontCountry().country(),
    signal: gpcNotSignalled
  })
}

export class GeoCookieFactory {
  current = (): GeoCookie | NoGeoCookie => {
    if (canUseDOM) {
      return this.fromDocument()
    } else {
      return new NoGeoCookie()
    }
  }

  private fromDocument = (): GeoCookie | NoGeoCookie => {
    const match = document.cookie.match(/(?:^|; )wb-geo=([^;]*)/)
    if (match) {
      return this.parse(decodeURIComponent(match[1]))
    } else {
      return new NoGeoCookie()
    }
  }

  private parse = (value: string): GeoCookie => {
    const [code = ``, flag = `0`] = value.split(`.`)
    return new GeoCookie(this.country(code), this.signal(flag))
  }

  private country = (code: string): ISOCountry => {
    if (/^[A-Z]{2}$/.test(code) && code !== unknownCountry) {
      return new ISOCountryFactory().fromString(code)
    } else {
      return new StorefrontCountry().country()
    }
  }

  private signal = (flag: string): PrivacySignal => {
    if (flag === `1`) {
      return gpcSignalled
    } else {
      return gpcNotSignalled
    }
  }
}
